import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Satellite, Gauge, Monitor, FileCode, Cloud, Boxes } from "lucide-react";
import { MaskedLines, FadeUp, Eyebrow, EASE, SectionHead } from "@/components/Reveal";
import DemoForm from "@/components/DemoForm";
import ServiceSchema from "@/components/ServiceSchema";
import JsonLd from "@/components/JsonLd";

const HERO_IMG =
  "https://images.unsplash.com/photo-1652303518379-c0ef1c9fb2b1?crop=entropy&cs=srgb&fm=jpg&q=85";

export const HIRE_INTERESTS = [
  "Hire — 3D on demand (one month)",
  "Hire — 3D on demand (three months)",
  "Hire — 3D on demand (six months)",
  "Hire — not sure yet",
];

const CHIPS = ["One install", "2D for the life of the machine", "3D by the month", "No lock-in"];

const WINDOWS = [
  ["One month", "$2,000"],
  ["Three months", "$4,700"],
  ["Six months", "$6,500"],
];

const HIRE_SCHEMA = {
  "@type": "Service",
  name: "CHCNAV 3D machine control hire — 3D on demand",
  serviceType: "Machine control hire",
  description:
    "Hire CHCNAV 3D excavator machine control by the month. One install gives you automatic 2D access for the life of the machine, with full 3D on demand — one, three or six months. Fitted and supported across Australia by Green Grade.",
  provider: { "@type": "LocalBusiness", name: "Green Grade", telephone: "+611300277689", url: "https://greengrade.au" },
  areaServed: "Australia",
  url: "https://greengrade.au/hire",
  offers: [
    { "@type": "Offer", name: "One month 3D access", price: "2000", priceCurrency: "AUD", availability: "https://schema.org/InStock", url: "https://greengrade.au/hire" },
    { "@type": "Offer", name: "Three months 3D access", price: "4700", priceCurrency: "AUD", availability: "https://schema.org/InStock", url: "https://greengrade.au/hire" },
    { "@type": "Offer", name: "Six months 3D access", price: "6500", priceCurrency: "AUD", availability: "https://schema.org/InStock", url: "https://greengrade.au/hire" },
  ],
};

const STEPS = [
  ["01", "We fit your machine", "Receivers, antennas, sensors and the screen go on once — fitted and commissioned by our crew, no re-work later."],
  ["02", "2D for the life of the machine", "Your install gives you automatic access to our 2D system for the life of the machine — no ongoing fee."],
  ["03", "3D on demand", "When the job hands you a model, unlock full 3D for exactly as long as you need it."],
];

const PLATFORM = [
  { icon: Satellite, title: "MC300 GNSS receivers", body: "RTK 8 mm + 1 ppm horizontal, 15 mm + 1 ppm vertical — IP67 rated for site dust and wash-down." },
  { icon: Gauge, title: "IS300 IMUs · 100 Hz", body: "Sensors on boom, stick and bucket at ±0.05° static — the display never lags the boom." },
  { icon: Monitor, title: 'MCPad300 10.1" display', body: "1920 × 1200 and IP67 — daylight-readable colour cut/fill maps in the cab." },
  { icon: FileCode, title: "Opens the job files", body: "LandXML, DWG and DXF directly, plus .dc, .cal, .jxl, .lok and .loc coordinate files." },
  { icon: Cloud, title: "iSite cloud", body: "Over-the-air updates and remote troubleshooting keep downtime near zero." },
  { icon: Boxes, title: "Tilt-rotator ready", body: "Automated tilt control for Engcon and Steelwrist tilt-rotators, tilt buckets and standard buckets." },
];

const FAQS = [
  ["What do I get when I hire 3D?", 'Full 3D on the TX73 — model-driven guidance with live colour cut/fill on the 10.1" display, opening LandXML, DWG and DXF files. It unlocks on top of the 2D system that\'s already yours for the life of the machine.'],
  ["What does 3D hire cost?", "One month is $2,000, three months $4,700 and six months $6,500 — indicative rates, confirmed for your machine and site."],
  ["Is there any lock-in?", "No. Hire 3D for exactly as long as the job needs, and at any stage you can purchase the system outright for an additional fee."],
  ["How long does the install take?", "Receivers, antennas, sensors and the screen are fitted and commissioned by our crew — one install, done properly, with no re-work later."],
  ["Do I need 3D design files?", "Only when the job hands you a model — roads, pads and sites with contours. For levels, falls and depth against a benchmark, the 2D tier you get with the install already covers it."],
];

const HIRE_FAQ_SCHEMA = {
  "@type": "FAQPage",
  mainEntity: FAQS.map(([q, a]) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

export default function Hire() {
  const scrollToForm = () => document.getElementById("hire-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <main>
      <ServiceSchema service={HIRE_SCHEMA} />
      <section data-testid="hire-hero" className="relative flex min-h-[88svh] items-end overflow-hidden">
        <motion.div
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0"
        >
          <img src={HERO_IMG} alt="Excavator working on a construction site" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/70" />
        </motion.div>
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto w-full max-w-[1600px] px-4 pb-20 pt-32 sm:px-8 lg:px-16">
          <FadeUp y={12}>
            <Eyebrow>
              <span>GREEN GRADE · 3D ON DEMAND — HIRE</span>
            </Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.15}
            lines={["Hire 3D.", { text: "When the job asks.", lineClass: "italic text-neon" }]}
            className="mt-4 font-display text-6xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-8xl lg:text-9xl"
          />
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            We fit your machine with receivers, antennas, sensors and the screen. That gives you automatic access to
            our 2D system for the life of the machine — and full 3D on demand, by the month, whenever the work needs it.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <span
                key={c}
                className="border border-signal/40 bg-ink/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon backdrop-blur-sm"
              >
                {c}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <button
              type="button"
              data-testid="hire-hero-enquire-cta"
              onClick={scrollToForm}
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
            >
              Enquire about hire
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <Link
              to="/#quote"
              data-testid="hire-hero-quote-cta"
              className="inline-flex items-center gap-3 border border-signal/50 px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-neon transition-colors hover:bg-signal/10"
            >
              Build a quote
            </Link>
          </div>
        </div>
      </section>

      <section
        id="how"
        className="border-y border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-32"
        data-testid="hire-how"
      >
        <div className="mx-auto max-w-[1600px]">
          <SectionHead
            kicker="The hire model"
            title="How 3D on demand works."
            body="One install. 2D for the life of the machine. Full 3D when the job asks for it — by the month."
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {STEPS.map(([n, t, b], i) => (
              <FadeUp key={n} delay={i * 0.08}>
                <div className="h-full border border-signal/40 bg-surface p-8 sm:p-10">
                  <p className="text-stroke font-display text-4xl font-black leading-none">{n}</p>
                  <h3 className="mt-4 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
                    {t}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-fog sm:text-base">{b}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2} className="mt-6">
            <div className="grid gap-6 sm:grid-cols-3" data-testid="hire-windows">
              {WINDOWS.map(([m, price]) => (
                <div
                  key={m}
                  data-testid={`hire-window-${m.split(" ")[0].toLowerCase()}`}
                  className="border border-signal/40 bg-surface p-6 text-center transition-colors duration-300 hover:border-signal sm:p-8"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">3D access</p>
                  <h3 className="mt-2 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
                    {m}
                  </h3>
                  <p className="mt-4 font-display text-4xl font-black text-neon" data-testid={`hire-rate-${m.split(" ")[0].toLowerCase()}`}>
                    {price}
                  </p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">indicative rate</p>
                </div>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.25}>
            <p className="mt-10 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Rates are indicative — we confirm the final figure for your machine and site. At any stage you can
              purchase the system outright for an additional fee — your call, no lock-in.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="hire-platform">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead
            kicker="What's on the machine"
            title="The TX73 platform."
            body="Hire or own, the hardware is the full CHCNAV MCNAV TX73 — the same platform running on Green Grade installs across Australia."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PLATFORM.map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.06}>
                <div className="h-full border border-line bg-surface p-6 transition-colors duration-300 hover:border-signal/50 sm:p-8">
                  <f.icon className="h-6 w-6 text-signal" />
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight text-white">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-fog">{f.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section id="hire-faq" className="border-t border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="hire-faq">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead
            kicker="Common questions"
            title="Hire, in plain terms."
            body="The short answers. For anything else, ask us below — a specialist calls you back within one business day."
          />
          <div className="mt-12 border-t border-line">
            {FAQS.map(([q, a], i) => (
              <FadeUp key={q} delay={i * 0.05} y={16}>
                <details className="group border-b border-line py-5" data-testid={`hire-faq-${i}`}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-6 font-display text-lg font-bold uppercase tracking-tight text-white transition-colors hover:text-neon sm:text-xl">
                    {q}
                    <span className="shrink-0 font-mono text-xl text-signal transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 max-w-2xl text-sm leading-relaxed text-fog sm:text-base">{a}</p>
                </details>
              </FadeUp>
            ))}
          </div>
          <JsonLd schema={HIRE_FAQ_SCHEMA} testid="hire-faq-schema" />
        </div>
      </section>

      <section
        id="hire-form"
        className="border-t border-line bg-ink px-4 py-20 sm:px-8 lg:px-16 lg:py-32"
        data-testid="hire-form-section"
      >
        <div className="mx-auto max-w-[1600px]">
          <SectionHead
            kicker="Enquire"
            title="Ask about hire."
            body="Tell us your machine and how long you need 3D — we'll come back with hire options within one business day."
          />
          <FadeUp delay={0.12} className="mt-12">
            <DemoForm interests={HIRE_INTERESTS} defaultInterest={HIRE_INTERESTS[0]} submitLabel="Enquire about hire" />
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
